/**
 * Hyperframes port of `src/scenes/intro/brandFlash.tsx`.
 * White screen-flash, then logo lands with motion blur.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import { escapeHtml } from "../../composition.js";
import { accentFor, makeTl, clipOutroFade, combineSceneFragments, withAlpha, colors as themeColors } from "../../reel/sceneShared.js";
import { ci } from "../../../brand/theme.js";

const SUPPORTED_IMG_EXT = [".png", ".jpg", ".jpeg", ".webp", ".svg"];

function resolveLogoSrc(): string | null {
  const logoPath = ci.logo ?? "src/brand/logo.svg";
  const ext = logoPath.toLowerCase().slice(logoPath.lastIndexOf("."));
  if (!SUPPORTED_IMG_EXT.includes(ext)) return null;
  const filename = logoPath.split("/").pop() ?? "logo.svg";
  return `assets/brand/${filename}`;
}

export const brandFlash: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const logoH = ctx.format === "9:16" ? 280 : 220;
  const logoSrc = resolveLogoSrc();

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const flashId = `${clipPrefix}-flash`;
  const logoBoxId = `${clipPrefix}-logo`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "solid", accent });

  const logoMarkup = logoSrc
    ? `<img src="${escapeHtml(logoSrc)}" alt="logo" style="height:${logoH}px;width:auto;object-fit:contain;" />`
    : `<div style="width:${logoH}px;height:${logoH}px;border-radius:9999px;background:${themeColors.fg};"></div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;">
  <div style="position:absolute;inset:0;background:radial-gradient(circle at 50% 50%, ${withAlpha(accent, 0.55)}, transparent 65%);"></div>
  <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">
    <div id="${logoBoxId}" style="filter:drop-shadow(0 0 80px ${withAlpha(accent, 0.5)});">
      ${logoMarkup}
    </div>
  </div>
  <div id="${flashId}" style="position:absolute;inset:0;background:${themeColors.fg};mix-blend-mode:screen;pointer-events:none;"></div>
</div>`;

  const init = [
    `gsap.set("#${flashId}", { opacity: 0 });`,
    `gsap.set("#${logoBoxId}", { scale: 0.6, opacity: 0, filter: "blur(12px)" });`,
  ].join("\n");

  const tweens = [
    t.to(`#${flashId}`, { opacity: 1, duration: 4 / 30, ease: "raw:\"power2.out\"" }, 0),
    t.to(`#${flashId}`, { opacity: 0, duration: 4 / 30, ease: "raw:\"power2.in\"" }, 4 / 30),
    t.to(`#${logoBoxId}`, { scale: 1, opacity: 1, filter: "blur(0px)", duration: 0.73, ease: "raw:\"back.out(1.6)\"" }, 6 / 30),
    clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 8),
  ];

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
